import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import Modal from "./Modal";
import OptimizedImage from "@/components/common/OptimizedImage";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: Array<{ url: string; id?: string; is_main?: boolean }>;
  title: string;
  currentImageIndex: number;
  onImageChange?: (index: number) => void;
  /** Optional caption/footer below the image viewer (e.g. photo coach examples). */
  renderCaption?: (index: number) => ReactNode;
}

export default function ImageModal({
  isOpen,
  onClose,
  images,
  title,
  currentImageIndex,
  onImageChange,
  renderCaption,
}: ImageModalProps) {
  const controlSurfaceClass =
    "rounded-md bg-black/55 p-2 text-white backdrop-blur-sm transition-opacity hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white";

  const topControlStyle = {
    top: "max(1rem, env(safe-area-inset-top))",
  } as const;

  const bottomControlStyle = {
    bottom: "max(1rem, env(safe-area-inset-bottom))",
  } as const;

  // Ensure images is an array and filter out any invalid images
  const validImages = (images || []).filter((img) => img && img.url);

  const safeCurrentIndex = Math.min(
    Math.max(0, currentImageIndex),
    validImages.length - 1,
  );
  const currentImage = validImages[safeCurrentIndex];

  // State for zoom and pan functionality
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [startPanPosition, setStartPanPosition] = useState({ x: 0, y: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 1200,
    height: 800,
  });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isImageTransitioning, setIsImageTransitioning] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const isGestureActiveRef = useRef(false);
  const suppressCloseUntilRef = useRef(0);
  const rafPanRef = useRef<number | null>(null);
  const pendingPanRef = useRef<{ x: number; y: number } | null>(null);
  const lastTapTimeRef = useRef(0);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const touchLastRef = useRef<{ x: number; y: number } | null>(null);
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef(1);

  const getWrappedIndex = useCallback(
    (index: number) => {
      if (!validImages.length) return 0;
      return (index + validImages.length) % validImages.length;
    },
    [validImages.length],
  );

  const queuePanUpdate = useCallback((nextPan: { x: number; y: number }) => {
    pendingPanRef.current = nextPan;
    if (rafPanRef.current !== null) return;
    rafPanRef.current = window.requestAnimationFrame(() => {
      if (pendingPanRef.current) {
        setPanPosition(pendingPanRef.current);
      }
      pendingPanRef.current = null;
      rafPanRef.current = null;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (rafPanRef.current !== null) {
        window.cancelAnimationFrame(rafPanRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const container = imageContainerRef.current;
    if (!container) return;

    const updateContainerSize = () => {
      const rect = container.getBoundingClientRect();
      setContainerSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateContainerSize();
    const resizeObserver = new ResizeObserver(updateContainerSize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen]);

  // Reset zoom and pan when opening modal or changing images
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, currentImageIndex]);

  const fittedImageSize = useMemo(() => {
    const { width: containerWidth, height: containerHeight } = containerSize;
    const { width: iw, height: ih } = imageNaturalSize;
    if (!containerWidth || !containerHeight || !iw || !ih) {
      return { width: 0, height: 0 };
    }
    const scale = Math.min(containerWidth / iw, containerHeight / ih);
    return {
      width: iw * scale,
      height: ih * scale,
    };
  }, [containerSize, imageNaturalSize]);

  const panBounds = useMemo(() => {
    const { width: containerWidth, height: containerHeight } = containerSize;
    if (!containerWidth || !containerHeight) return { maxX: 0, maxY: 0 };
    const scaledWidth = fittedImageSize.width * zoomLevel;
    const scaledHeight = fittedImageSize.height * zoomLevel;
    return {
      maxX: Math.max(0, (scaledWidth - containerWidth) / 2),
      maxY: Math.max(0, (scaledHeight - containerHeight) / 2),
    };
  }, [containerSize, zoomLevel, fittedImageSize]);

  const imageOverflowsViewport = panBounds.maxX > 0 || panBounds.maxY > 0;
  const canPan = imageOverflowsViewport || zoomLevel > 1;

  const clampPan = useCallback(
    (nextPan: { x: number; y: number }) => ({
      x: Math.min(panBounds.maxX, Math.max(-panBounds.maxX, nextPan.x)),
      y: Math.min(panBounds.maxY, Math.max(-panBounds.maxY, nextPan.y)),
    }),
    [panBounds.maxX, panBounds.maxY],
  );

  // Function to zoom image
  const handleZoom = useCallback((zoomIn: boolean) => {
    setZoomLevel((prevZoom) => {
      const newZoom = zoomIn
        ? Math.min(prevZoom + 0.25, 3)
        : Math.max(prevZoom - 0.25, 1);
      if (newZoom === 1) {
        setPanPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  // Reset zoom
  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (!currentImage?.url) return;
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (cancelled) return;
      setImageNaturalSize({
        width: Math.max(1, img.naturalWidth || 1200),
        height: Math.max(1, img.naturalHeight || 800),
      });
    };
    img.src = currentImage.url;
    return () => {
      cancelled = true;
    };
  }, [currentImage?.url]);

  useEffect(() => {
    if (!isOpen || validImages.length <= 1) return;
    const prevIndex = getWrappedIndex(safeCurrentIndex - 1);
    const nextIndex = getWrappedIndex(safeCurrentIndex + 1);
    [validImages[prevIndex]?.url, validImages[nextIndex]?.url]
      .filter(Boolean)
      .forEach((url) => {
        const image = new window.Image();
        image.src = url as string;
      });
  }, [isOpen, safeCurrentIndex, validImages, getWrappedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const startTimer = window.setTimeout(() => {
      setIsImageTransitioning(true);
    }, 0);
    const timeoutId = window.setTimeout(() => {
      setIsImageTransitioning(false);
    }, 120);
    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(timeoutId);
    };
  }, [safeCurrentIndex, isOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (zoomLevel <= 1) {
        setPanPosition({ x: 0, y: 0 });
        return;
      }
      setPanPosition((prev) => clampPan(prev));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [zoomLevel, panBounds.maxX, panBounds.maxY, clampPan]);

  const goToImage = useCallback(
    (nextIndex: number) => {
      if (!validImages.length) return;
      onImageChange?.(getWrappedIndex(nextIndex));
    },
    [getWrappedIndex, onImageChange, validImages.length],
  );

  const goToNextImage = useCallback(() => {
    goToImage(safeCurrentIndex + 1);
  }, [goToImage, safeCurrentIndex]);

  const goToPrevImage = useCallback(() => {
    goToImage(safeCurrentIndex - 1);
  }, [goToImage, safeCurrentIndex]);

  const requestClose = useCallback(() => {
    if (
      isGestureActiveRef.current ||
      Date.now() < suppressCloseUntilRef.current
    ) {
      return;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const targetTag = target?.tagName?.toLowerCase();
      if (
        targetTag === "input" ||
        targetTag === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          goToPrevImage();
          break;
        case "ArrowRight":
          event.preventDefault();
          goToNextImage();
          break;
        case "+":
        case "=":
          event.preventDefault();
          handleZoom(true);
          break;
        case "-":
        case "_":
          event.preventDefault();
          handleZoom(false);
          break;
        case "0":
          event.preventDefault();
          resetZoom();
          break;
        case "Escape":
          event.preventDefault();
          requestClose();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    goToPrevImage,
    goToNextImage,
    handleZoom,
    isOpen,
    requestClose,
    resetZoom,
  ]);

  // Pan handlers
  const handlePanStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!canPan) return;
      setIsPanning(true);
      setStartPanPosition({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y,
      });
    },
    [canPan, panPosition],
  );

  const handlePanMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isPanning && canPan) {
        queuePanUpdate(
          clampPan({
            x: e.clientX - startPanPosition.x,
            y: e.clientY - startPanPosition.y,
          }),
        );
      }
    },
    [
      isPanning,
      canPan,
      startPanPosition.x,
      startPanPosition.y,
      clampPan,
      queuePanUpdate,
    ],
  );

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch events for mobile panning
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      isGestureActiveRef.current = true;
      suppressCloseUntilRef.current = Date.now() + 220;
      if (e.touches.length === 2) {
        pinchStartDistanceRef.current = getTouchDistance(e.touches);
        pinchStartZoomRef.current = zoomLevel;
        return;
      }
      const now = Date.now();
      const isDoubleTap = now - lastTapTimeRef.current < 260;
      lastTapTimeRef.current = now;
      if (isDoubleTap) {
        const targetZoom = zoomLevel > 1 ? 1 : 2;
        setZoomLevel(targetZoom);
        if (targetZoom === 1) {
          setPanPosition({ x: 0, y: 0 });
        }
        return;
      }
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: now,
      };
      touchLastRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      if (canPan) {
        setIsPanning(true);
        setStartPanPosition({
          x: e.touches[0].clientX - panPosition.x,
          y: e.touches[0].clientY - panPosition.y,
        });
      }
    },
    [canPan, panPosition, zoomLevel],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2 && pinchStartDistanceRef.current) {
        const distance = getTouchDistance(e.touches);
        if (!distance) return;
        const nextZoom = Math.min(
          3,
          Math.max(
            1,
            (distance / pinchStartDistanceRef.current) *
              pinchStartZoomRef.current,
          ),
        );
        setZoomLevel(nextZoom);
        e.preventDefault();
        return;
      }
      if (isPanning && canPan) {
        e.preventDefault();
        queuePanUpdate(
          clampPan({
            x: e.touches[0].clientX - startPanPosition.x,
            y: e.touches[0].clientY - startPanPosition.y,
          }),
        );
      } else if (e.touches.length === 1) {
        touchLastRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      }
    },
    [
      isPanning,
      canPan,
      startPanPosition.x,
      startPanPosition.y,
      clampPan,
      queuePanUpdate,
    ],
  );

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    const startTouch = touchStartRef.current;
    const lastTouch = touchLastRef.current;
    if (startTouch && zoomLevel <= 1 && !imageOverflowsViewport) {
      const deltaX = startTouch.x - (lastTouch?.x ?? startTouch.x);
      const deltaY = startTouch.y - (lastTouch?.y ?? startTouch.y);
      const elapsed = Date.now() - startTouch.time;
      const threshold = 48;
      if (
        elapsed < 550 &&
        Math.abs(deltaX) > threshold &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        if (deltaX > 0) {
          goToNextImage();
        } else {
          goToPrevImage();
        }
      }
    }
    pinchStartDistanceRef.current = null;
    touchStartRef.current = null;
    touchLastRef.current = null;
    window.setTimeout(() => {
      isGestureActiveRef.current = false;
    }, 120);
  }, [goToNextImage, goToPrevImage, zoomLevel, imageOverflowsViewport]);

  // Handle wheel event for zooming with scroll wheel
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (!canPan) return;
      e.preventDefault();
      queuePanUpdate(
        clampPan({
          x: panPosition.x - e.deltaX,
          y: panPosition.y - e.deltaY,
        }),
      );
    },
    [canPan, clampPan, panPosition.x, panPosition.y, queuePanUpdate],
  );

  // Image navigation handlers
  const nextImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      goToNextImage();
    },
    [goToNextImage],
  );

  const prevImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      goToPrevImage();
    },
    [goToPrevImage],
  );

  // If there are no valid images, don't render the modal
  if (!validImages.length) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      maxWidth="5xl"
      showCloseIcon={false}
      className="p-0 bg-transparent shadow-none"
      bodyClassName={renderCaption ? "flex h-[85vh] flex-col" : "h-[80vh]"}
    >
      <ErrorBoundary>
        <div
          className={
            renderCaption
              ? "flex h-full min-h-0 flex-col"
              : "relative h-full w-full"
          }
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={
              renderCaption
                ? "relative min-h-0 flex-1"
                : "relative h-full w-full"
            }
          >
            {/* Close button */}
            <button
              type="button"
              onClick={requestClose}
              className={`absolute right-4 z-10 ${controlSurfaceClass}`}
              style={topControlStyle}
              aria-label="Close image modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Zoom controls */}
            <div
              className="absolute left-4 z-10 flex space-x-2"
              style={topControlStyle}
            >
              <button
                type="button"
                onClick={() => handleZoom(false)}
                className={controlSurfaceClass}
                title="Zoom out"
                aria-label="Zoom out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <button
                type="button"
                onClick={resetZoom}
                className={controlSurfaceClass}
                title="Reset zoom"
                aria-label="Reset zoom"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  <path d="M9 10v4"></path>
                  <path d="M15 10v4"></path>
                  <path d="M10 9h4"></path>
                  <path d="M10 15h4"></path>
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleZoom(true)}
                className={controlSurfaceClass}
                title="Zoom in"
                aria-label="Zoom in"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-6 h-6"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>

            {/* Zoom level indicator */}
            <div
              className="absolute left-4 z-10 rounded-md bg-black/55 px-3 py-1 text-white backdrop-blur-sm"
              style={bottomControlStyle}
            >
              {Math.round(zoomLevel * 100)}%
            </div>
            <div
              className="absolute right-4 z-10 rounded-md bg-black/55 px-3 py-1 text-sm text-white backdrop-blur-sm"
              style={bottomControlStyle}
            >
              {safeCurrentIndex + 1} / {validImages.length}
            </div>
            <div
              className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-md bg-black/45 px-2.5 py-1 text-xs text-white backdrop-blur-sm"
              style={bottomControlStyle}
            >
              {imageOverflowsViewport && zoomLevel <= 1
                ? "Drag to see full image"
                : "Use arrows/swipe to navigate"}
            </div>

            {/* Current Image with pan functionality */}
            <div
              ref={imageContainerRef}
              className="relative w-full h-full overflow-hidden"
              onMouseDown={handlePanStart}
              onMouseMove={handlePanMove}
              onMouseUp={handlePanEnd}
              onMouseLeave={handlePanEnd}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onWheel={handleWheel}
              style={{
                cursor: canPan ? (isPanning ? "grabbing" : "grab") : "auto",
              }}
            >
              <div className="flex h-full w-full items-center justify-center">
                <div
                  style={{
                    transform: `translate(${panPosition.x}px, ${panPosition.y}px)`,
                    transition: isPanning ? "none" : "transform 0.2s ease-out",
                    width:
                      fittedImageSize.width > 0
                        ? fittedImageSize.width
                        : "100%",
                    height:
                      fittedImageSize.height > 0
                        ? fittedImageSize.height
                        : "100%",
                  }}
                >
                  <div
                    style={{
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: "center",
                      transition: isPanning
                        ? "none"
                        : "transform 0.2s ease-out",
                      width: "100%",
                      height: "100%",
                    }}
                    className={`transition-opacity duration-150 ${
                      isImageTransitioning ? "opacity-90" : "opacity-100"
                    }`}
                  >
                    <OptimizedImage
                      src={validImages[safeCurrentIndex]?.url}
                      alt={`${title} image ${safeCurrentIndex + 1}`}
                      width={imageNaturalSize.width}
                      height={imageNaturalSize.height}
                      objectFit="contain"
                      className="h-full w-full rounded-lg"
                      priority={true}
                      sizes="100vw"
                      usage="detail-zoom"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            {validImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevImage}
                  className={`absolute left-4 top-1/2 -translate-y-1/2 ${controlSurfaceClass}`}
                  aria-label="View previous image"
                >
                  <ChevronLeftIcon className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={nextImage}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${controlSurfaceClass}`}
                  aria-label="View next image"
                >
                  <ChevronRightIcon className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {renderCaption ? (
            <div className="flex-shrink-0 border-t border-border bg-surface px-4 py-3">
              {renderCaption(safeCurrentIndex)}
            </div>
          ) : null}
        </div>
      </ErrorBoundary>
    </Modal>
  );
}
